import {useFormikContext} from 'formik';
import {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import ImageCropModal from '../../../../../components/imageCropModal';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import usePostUploadCreditLoanImg from '../../../../../services/hookService/postUploadCreditLoanImg/usePostUploadCreditLoanImg';
import ImageUploadBox from '../../../../shopping-basket/cash-payment/card-to-card-pay/ImageUploadBox';

import useDeleteDocumentImg from '../../../../../services/hookService/deleteDocumentImg/useDeleteDocumentImg';
import {PersonPrefix} from '../credit-loan/Forms/MultiPersonForm/MultiPersonValidation';
import {IBankDocsGeneralForm} from '../credit-loan/LoanDocsPage';

type TChequeImageField =
  | 'first_check_image'
  | 'second_check_image'
  | 'third_check_image';

interface DocumentMultiImageUploaderProps {
  title?: string;

  loanRequestGuid: string;

  loanRuleGuid: string;

  loanDocumentGuid: string;

  personPrefix?: PersonPrefix;

  fieldName?: TChequeImageField;

  onImageUploaded?: (
    file: File | null,
    uploadedGuid: string | null,
    field: TChequeImageField,
  ) => void;

  className?: string;
}

const DocumentMultiImageUploader = ({
  loanRequestGuid,
  loanRuleGuid,
  loanDocumentGuid,
  personPrefix,
  fieldName,
  onImageUploaded,
  className = '',
}: DocumentMultiImageUploaderProps) => {
  const formik =
    personPrefix && fieldName ? useFormikContext<IBankDocsGeneralForm>() : null;

  const instanceId = useRef(
    `multi-uploader-${Date.now()}-${Math.random()}`,
  ).current;

  const {mutate: deleteDocumentImg, isLoading: isDeletingImgLoading} =
    useDeleteDocumentImg();

  const [uploadedImage, setUploadedImage] = useState<File | null>(null);

  const [uploadedBackendGuid, setUploadedBackendGuid] = useState('');

  const [showCropModal, setShowCropModal] = useState(false);

  const [currentImage, setCurrentImage] = useState<File | null>(null);

  useEffect(() => {
    if (!formik || !personPrefix || !fieldName) return;

    const path = `${personPrefix}.${fieldName}`;

    const value = formik.getFieldMeta(path)?.value;

    setUploadedImage(value instanceof File ? value : null);
  }, [formik?.values, personPrefix, fieldName]);

  const {mutate: uploadFile, isLoading} = usePostUploadCreditLoanImg({
    onSuccess: data => {
      if (!currentImage || !personPrefix || !fieldName) {
        return;
      }

      setUploadedImage(currentImage);

      setUploadedBackendGuid(data?.guid ?? '');

      if (formik) {
        formik.setFieldValue(`${personPrefix}.${fieldName}`, currentImage);
      }

      onImageUploaded?.(currentImage, data?.guid ?? null, fieldName);

      setShowCropModal(false);
    },
  });

  const handleCropComplete = useCallback(
    (croppedImage: File) => {
      uploadFile({
        loan_request: loanRequestGuid,
        loan_rule: loanRuleGuid,
        loan_document: loanDocumentGuid,
        file: croppedImage,
      });
    },
    [loanRequestGuid, loanRuleGuid, loanDocumentGuid, uploadFile],
  );

  const handleImageChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setCurrentImage(file);

    setShowCropModal(true);

    e.target.value = '';
  }, []);

  const removeCroppedImage = useCallback(async () => {
    const clearLocalImage = () => {
      setUploadedImage(null);
      setUploadedBackendGuid('');

      if (formik && personPrefix && fieldName) {
        formik.setFieldValue(`${personPrefix}.${fieldName}`, null);
      }

      onImageUploaded?.(null, null, fieldName!);
    };

    if (!uploadedBackendGuid) {
      clearLocalImage();
      return;
    }

    deleteDocumentImg(uploadedBackendGuid, {
      onSuccess: () => clearLocalImage(),
    });
  }, [uploadedBackendGuid, formik, personPrefix, fieldName, onImageUploaded]);
  if (isLoading || isDeletingImgLoading) {
    return (
      <div className="flex items-center justify-center w-full py-4">
        <SpinnerLoading />
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex gap-2 mt-3">
        <ImageUploadBox
          variant="sm"
          croppedImageFile={uploadedImage}
          onImageRemove={removeCroppedImage}
          onImageChange={handleImageChange}
          inputId={`${instanceId}-${fieldName}`}
        />
      </div>

      {showCropModal &&
        currentImage &&
        createPortal(
          <ImageCropModal
            originalFile={currentImage}
            onComplete={handleCropComplete}
            onClose={() => setShowCropModal(false)}
          />,
          document.body,
        )}
    </div>
  );
};

export default DocumentMultiImageUploader;
